import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { FileCheck } from 'lucide-react';
export default function ClinicalNegligence() {
  return <main className="min-h-screen bg-white" data-id="element-973">
      <Helmet data-id="element-974">
        <title data-id="element-975">
          Clinical Negligence Psychiatric Assessments | Medicolegal Psychiatry
        </title>
        <meta name="description" content="Independent psychiatric assessments for clinical negligence cases, including causation, psychiatric injury, prognosis, and expert witness reporting." data-id="element-976" />
        <meta name="keywords" content="clinical negligence psychiatric assessments, psychiatric expert witness, medico-legal psychiatry, psychiatric injury, causation, prognosis" data-id="element-977" />
        <script type="application/ld+json" data-id="element-978">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "Clinical Negligence Psychiatric Assessments",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Clinical Negligence Psychiatric Assessment",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact"
              },
              "serviceOutput": "Expert Psychiatric Reports for Clinical Negligence Proceedings"
            }
          `}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-979">
        <div className="container mx-auto px-4" data-id="element-980">
          <div className="flex items-center justify-center mb-4" data-id="element-981">
            <FileCheck className="h-12 w-12 mr-4" data-id="element-982" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-983">
              Clinical Negligence Cases
            </h1>
          </div>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-984">
            Independent psychiatric assessments for clinical negligence matters
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12" data-id="element-985">
        <div className="container mx-auto px-4" data-id="element-986">
          <div className="max-w-3xl mx-auto" data-id="element-987">
            <div className="prose max-w-none" data-id="element-988">
              {/* Intro */}
              <p className="text-slate-dark mb-6 text-lg" data-id="element-989">
                We provide independent psychiatric assessments for clinical
                negligence matters where concerns have been raised about
                diagnosis, treatment, aftercare, risk management, or wider
                mental health care. Our reports are prepared to assist
                solicitors, courts, and tribunals by giving clear expert opinion
                on psychiatric injury, causation, impact, and prognosis.
              </p>

              {/* What This Service Covers */}
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-990">
                What This Service Covers
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-991">
                Our clinical negligence work may include cases involving:
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-992">
                <li className="mb-2" data-id="element-993">
                  Delayed or missed diagnosis of psychiatric illness
                </li>
                <li className="mb-2" data-id="element-994">
                  Inappropriate psychiatric treatment or medication management
                </li>
                <li className="mb-2" data-id="element-995">
                  Failures in inpatient or community mental health care
                </li>
                <li className="mb-2" data-id="element-996">
                  Suicide and self-harm risk management concerns
                </li>
                <li className="mb-2" data-id="element-997">
                  Inadequate follow-up, discharge planning, or safeguarding
                </li>
                <li className="mb-2" data-id="element-998">
                  Deterioration in mental health linked to alleged negligent
                  care
                </li>
                <li data-id="element-999">
                  Capacity, vulnerability, and psychiatric consequences arising
                  from poor care
                </li>
              </ul>

              {/* How We Assist */}
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-1000">
                How We Assist
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-1001">
                Our role is to provide balanced, independent psychiatric opinion
                on the mental health aspects of a clinical negligence claim.
                This may include reviewing medical records, assessing the
                individual, considering breach and causation issues where
                appropriate, and setting out the psychiatric consequences in a
                clear and structured report.
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1002">
                <li className="mb-2" data-id="element-1003">
                  Review of psychiatric and relevant medical records
                </li>
                <li className="mb-2" data-id="element-1004">
                  Independent assessment and expert opinion
                </li>
                <li className="mb-2" data-id="element-1005">
                  Consideration of causation and psychiatric impact
                </li>
                <li className="mb-2" data-id="element-1006">
                  Diagnosis, prognosis, and treatment recommendations where
                  required
                </li>
                <li data-id="element-1007">
                  Clear reporting suitable for litigation and expert witness
                  purposes
                </li>
              </ul>

              {/* Who This Service Is For */}
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-1008">
                Who This Service Is For
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-1009">
                This service is designed for solicitors, legal teams, and
                organisations requiring expert psychiatric input in clinical
                negligence matters.
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1010">
                <li className="mb-2" data-id="element-1011">Claimant solicitors</li>
                <li className="mb-2" data-id="element-1012">Defendant solicitors</li>
                <li className="mb-2" data-id="element-1013">Courts and tribunals</li>
                <li data-id="element-1014">Agencies instructing psychiatric experts</li>
              </ul>

              {/* Why Instruct Medicolegal Psychiatry */}
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-1015">
                Why Instruct Medicolegal Psychiatry
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-1016">
                Medicolegal Psychiatry provides specialist psychiatric expertise
                with a clear focus on independence, clarity, and reliability.
                Reports are prepared with the needs of legal professionals in
                mind and written in a structured format suitable for scrutiny.
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1017">
                <li className="mb-2" data-id="element-1018">Independent and evidence-based opinion</li>
                <li className="mb-2" data-id="element-1019">
                  Clear, well-structured psychiatric reporting
                </li>
                <li className="mb-2" data-id="element-1020">
                  Experience in complex medico-legal matters
                </li>
                <li className="mb-2" data-id="element-1021">Professional, responsive communication</li>
                <li data-id="element-1022">Reports tailored to the needs of litigation</li>
              </ul>

              {/* Related Services */}
              <div className="mb-10 border-t border-gray-200 pt-8" data-id="element-1023">
                <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1024">
                  Explore Our Other Services
                </h3>
                <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-1025">
                  <Link to="/services/mental-capacity" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1026">
                    Mental Capacity Assessments
                  </Link>
                  <Link to="/services/trauma-ptsd" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1027">
                    PTSD & Trauma Psychiatric Evaluations
                  </Link>
                  <Link to="/services/specialist" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1028">
                    Psychiatric Reports for Courts & Tribunals
                  </Link>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-gray-50 rounded-lg p-6 shadow-md mb-8" data-id="element-1029">
                <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1030">
                  Need a Psychiatric Assessment for a Clinical Negligence
                  Matter?
                </h3>
                <p className="text-slate-dark mb-4" data-id="element-1031">
                  If you require an independent psychiatric expert for a
                  clinical negligence case, please get in touch to discuss the
                  instruction, timescales, and scope of assessment.
                </p>
                <Button variant="primary" href="/contact" data-id="element-1032">
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}